import axios from 'axios'
import { reqHeaders } from '@shared/config'
import { Payee } from '@root/api/ump-catalog-manager'
import { baseUmpPath } from '@root/api/api'
import { delay } from '@shared/lib'
import { axiosProxyInstance } from '@api/axios.cjs'
import { transformAmountByCommissionData } from '../lib/transformDenominationAmountByCommissionData'
import { CommissionDataByServiceIdType, DenominationAmountByServiceIdType } from './denomination.types'

const REQUEST_DELAY_MS = 100
const BATCH_SIZE = 4
const MAX_ATTEMPTS = 3
const RETRYABLE_CODES = new Set([
  'ENOTFOUND',
  'EAI_AGAIN',
  'ECONNRESET',
  'ECONNREFUSED',
  'ETIMEDOUT',
  'ECONNABORTED',
  'ERR_NETWORK',
  'EPIPE',
])
const RETRYABLE_STATUSES = new Set([407, 408, 429, 500, 502, 503, 504])

const isRetryablePriceError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false

  if (error.code && RETRYABLE_CODES.has(error.code)) return true

  return error.response?.status !== undefined && RETRYABLE_STATUSES.has(error.response.status)
}

const fetchPriceAttempt = async (path: string, providerName: string | undefined, attempt: number): Promise<string> => {
  try {
    const priceRes = await axiosProxyInstance.get(path, { headers: reqHeaders })

    return priceRes.data.price === '' ? '' : priceRes.data.price
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    const code = axios.isAxiosError(error) ? error.code : undefined
    const message = error instanceof Error ? error.message : String(error)
    const detail = [status ? `status ${status}` : undefined, code, message].filter(Boolean).join(' ')

    console.warn(`getPrice ${path} (${providerName}), попытка ${attempt}/${MAX_ATTEMPTS}: ${detail}`)

    if (attempt >= MAX_ATTEMPTS || !isRetryablePriceError(error)) {
      return ''
    }

    await delay(REQUEST_DELAY_MS * attempt)

    return fetchPriceAttempt(path, providerName, attempt + 1)
  }
}

const fetchPrice = (path: string, providerName: string | undefined) => fetchPriceAttempt(path, providerName, 1)

export const getAmountInfoByServiceIds = async (
  denominations: Payee[],
  commissionData: CommissionDataByServiceIdType,
  isPubgMobile: boolean,
  isNonTopUp: boolean,
  isZeroFee: boolean,
  isGameWithPercentFee = false
) => {
  const amountInfoByServiceIds: DenominationAmountByServiceIdType = {}

  await denominations.reduce(async (previousPromise, _, index) => {
    await previousPromise

    // Если это начало нового батча, обрабатываем весь батч параллельно
    if (index % BATCH_SIZE === 0) {
      const batch = denominations.slice(index, index + BATCH_SIZE)

      const batchPromises = batch.map(async (batchDenomination) => {
        const activeDenominationParams =
          batchDenomination.params?.reduce((acc, param) => ({ ...acc, [param.name]: { value: param.defaultValue } }), {
            partnerId: { value: '' },
            serviceCode: { value: '' },
          }) || null

        const path = `${baseUmpPath}/anonymous/games/getPrice?serviceCode=${activeDenominationParams?.serviceCode.value}&partner=${activeDenominationParams?.partnerId.value}`
        const price = await fetchPrice(path, batchDenomination.providerName)

        amountInfoByServiceIds[batchDenomination.serviceId] = transformAmountByCommissionData(
          price,
          commissionData,
          isPubgMobile,
          isNonTopUp,
          isZeroFee,
          isGameWithPercentFee
        )
      })

      await Promise.all(batchPromises)

      // Небольшая задержка между батчами
      if (index + BATCH_SIZE < denominations.length) {
        await delay(REQUEST_DELAY_MS)
      }
    }

    return Promise.resolve()
  }, Promise.resolve())

  return amountInfoByServiceIds
}
